import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const AdminBackendsPage = () => {
  const { hasPermission } = useAuth();
  const [backends, setBackends] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBackend, setSelectedBackend] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAssignCarrierModal, setShowAssignCarrierModal] = useState(false);
  const [backendCarriers, setBackendCarriers] = useState([]);
  
  const [backendData, setBackendData] = useState({
    backend_name: '',
    host: '',
    user: '',
    password: '',
    database_name: '',
    description: '',
    api_key: '',
    port: 3306,
    is_active: 1
  });

  const [assignCarrierData, setAssignCarrierData] = useState({
    carrier_id: '',
    is_active: 1
  });

  // Get API base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const fetchBackends = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/backends`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setBackends(response.data.backends || []);
    } catch (err) {
      setError('Failed to fetch backends');
      console.error('Error fetching backends:', err);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  const fetchCarriers = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/carriers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setCarriers(response.data.carriers || []);
    } catch (err) {
      console.error('Error fetching carriers:', err);
    }
  }, [API_BASE_URL]);

  const fetchBackendCarriers = useCallback(async (backendId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/backends/${backendId}/carriers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setBackendCarriers(response.data.carriers || []);
    } catch (err) {
      console.error('Error fetching backend carriers:', err);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchBackends();
    fetchCarriers();
  }, [fetchBackends, fetchCarriers]);

  if (!hasPermission('system.admin')) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-12 bg-white rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const handleCreateBackend = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Validate required fields
      if (!backendData.backend_name || !backendData.host || !backendData.user || 
          !backendData.password || !backendData.database_name) {
        setError('All required fields must be filled');
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/admin/backends`, backendData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setShowCreateModal(false);
        resetBackendData();
        await fetchBackends();
        setError(null);
        alert('Backend created successfully!');
      }
    } catch (err) {
      console.error('Error creating backend:', err);
      setError(err.response?.data?.error || 'Failed to create backend');
    }
  };

  const handleUpdateBackend = async () => {
    try {
      const token = localStorage.getItem('authToken');

      // Exclude password from payload if left blank (avoid overwriting stored value)
      const payload = { ...backendData };
      if (!payload.password) {
        delete payload.password;
      }

      const response = await axios.put(
        `${API_BASE_URL}/admin/backends/${selectedBackend.backend_id}`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setShowEditModal(false);
        setSelectedBackend(null);
        resetBackendData();
        await fetchBackends();
        setError(null);
        alert('Backend updated successfully!');
      }
    } catch (err) {
      console.error('Error updating backend:', err);
      setError(err.response?.data?.error || 'Failed to update backend');
    }
  };

  const handleDeleteBackend = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await axios.delete(
        `${API_BASE_URL}/admin/backends/${selectedBackend.backend_id}`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setShowDeleteDialog(false);
        setSelectedBackend(null);
        await fetchBackends();
        alert('Backend deleted successfully!');
      }
    } catch (err) {
      console.error('Error deleting backend:', err);
      setError(err.response?.data?.error || 'Failed to delete backend');
    }
  };

  const handleAssignCarrier = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!assignCarrierData.carrier_id) {
        setError('Please select a carrier');
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/admin/backends/${selectedBackend.backend_id}/carriers`,
        assignCarrierData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        await fetchBackendCarriers(selectedBackend.backend_id);
        setAssignCarrierData({ carrier_id: '', is_active: 1 });
        setError(null);
        alert('Carrier assigned successfully!');
      }
    } catch (err) {
      console.error('Error assigning carrier:', err);
      setError(err.response?.data?.error || 'Failed to assign carrier');
    }
  };

  const handleRemoveCarrier = async (carrierId) => {
    if (!window.confirm('Are you sure you want to remove this carrier from the backend?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      
      const response = await axios.delete(
        `${API_BASE_URL}/admin/backends/${selectedBackend.backend_id}/carriers/${carrierId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        await fetchBackendCarriers(selectedBackend.backend_id);
        alert('Carrier removed successfully!');
      }
    } catch (err) {
      console.error('Error removing carrier:', err);
      setError(err.response?.data?.error || 'Failed to remove carrier');
    }
  };

  const resetBackendData = () => {
    setBackendData({
      backend_name: '',
      host: '',
      user: '',
      password: '',
      database_name: '',
      description: '',
      api_key: '',
      port: 3306,
      is_active: 1
    });
  };

  const openEditModal = (backend) => {
    setSelectedBackend(backend);
    setBackendData({
      backend_name: backend.backend_name,
      host: backend.host,
      user: backend.user,
      password: '', // Don't populate password for security
      database_name: backend.database_name,
      description: backend.description || '',
      api_key: backend.api_key || '',
      port: backend.port,
      is_active: backend.is_active
    });
    setShowEditModal(true);
  };

  const openManageCarriersModal = async (backend) => {
    setSelectedBackend(backend);
    await fetchBackendCarriers(backend.backend_id);
    setShowAssignCarrierModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading backends...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 h-full overflow-auto">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Backend Management</h1>
          <button
            onClick={() => {
              resetBackendData();
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            + Create Backend
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
            <button 
              onClick={() => setError(null)} 
              className="float-right text-red-700 hover:text-red-900"
            >
              ✕
            </button>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Host
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Database
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {backends.map((backend) => (
                <tr key={backend.backend_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {backend.backend_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>
                      <div className="font-medium">{backend.backend_name}</div>
                      {backend.description && (
                        <div className="text-xs text-gray-500">{backend.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {backend.host}:{backend.port}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {backend.database_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      backend.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {backend.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openEditModal(backend)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => openManageCarriersModal(backend)}
                        className="text-green-600 hover:text-green-800"
                      >
                        Carriers
                      </button>
                      <button
                        onClick={() => {
                          setSelectedBackend(backend);
                          setShowDeleteDialog(true);
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {backends.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No backends found. Create one to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Backend Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Create New Backend</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Backend Name *
                </label>
                <input
                  type="text"
                  value={backendData.backend_name}
                  onChange={(e) => setBackendData({...backendData, backend_name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., Ontario Sample Core"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={backendData.description}
                  onChange={(e) => setBackendData({...backendData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Optional description"
                  rows="2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key
                </label>
                <input
                  type="text"
                  value={backendData.api_key}
                  onChange={(e) => setBackendData({...backendData, api_key: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Upstream data API key"
                  maxLength={32}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Host *
                </label>
                <input
                  type="text"
                  value={backendData.host}
                  onChange={(e) => setBackendData({...backendData, host: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., db.example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Port
                </label>
                <input
                  type="number"
                  value={backendData.port}
                  onChange={(e) => setBackendData({...backendData, port: parseInt(e.target.value)})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="3306"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Database Name *
                </label>
                <input
                  type="text"
                  value={backendData.database_name}
                  onChange={(e) => setBackendData({...backendData, database_name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="e.g., panorama_db"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User *
                </label>
                <input
                  type="text"
                  value={backendData.user}
                  onChange={(e) => setBackendData({...backendData, user: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Database username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <input
                  type="password"
                  value={backendData.password}
                  onChange={(e) => setBackendData({...backendData, password: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Database password"
                />
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={backendData.is_active === 1}
                    onChange={(e) => setBackendData({...backendData, is_active: e.target.checked ? 1 : 0})}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetBackendData();
                  setError(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateBackend}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Create Backend
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Backend Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Edit Backend: {selectedBackend?.backend_name}</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Backend Name
                </label>
                <input
                  type="text"
                  value={backendData.backend_name}
                  onChange={(e) => setBackendData({...backendData, backend_name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={backendData.description}
                  onChange={(e) => setBackendData({...backendData, description: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key
                </label>
                <input
                  type="text"
                  value={backendData.api_key}
                  onChange={(e) => setBackendData({...backendData, api_key: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Upstream data API key"
                  maxLength={32}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Host
                </label>
                <input
                  type="text"
                  value={backendData.host}
                  onChange={(e) => setBackendData({...backendData, host: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Port
                </label>
                <input
                  type="number"
                  value={backendData.port}
                  onChange={(e) => setBackendData({...backendData, port: parseInt(e.target.value)})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Database Name
                </label>
                <input
                  type="text"
                  value={backendData.database_name}
                  onChange={(e) => setBackendData({...backendData, database_name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User
                </label>
                <input
                  type="text"
                  value={backendData.user}
                  onChange={(e) => setBackendData({...backendData, user: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password (leave empty to keep current)
                </label>
                <input
                  type="password"
                  value={backendData.password}
                  onChange={(e) => setBackendData({...backendData, password: e.target.value})}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Leave empty to keep current password"
                />
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={backendData.is_active === 1}
                    onChange={(e) => setBackendData({...backendData, is_active: e.target.checked ? 1 : 0})}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedBackend(null);
                  resetBackendData();
                  setError(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateBackend}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Update Backend
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the backend "{selectedBackend?.backend_name}"? 
              This will also remove all carrier associations with this backend.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setSelectedBackend(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteBackend}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Carriers Modal */}
      {showAssignCarrierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              Manage Carriers for: {selectedBackend?.backend_name}
            </h3>
            
            {/* Assign New Carrier Section */}
            <div className="mb-6 p-4 bg-gray-50 rounded">
              <h4 className="font-medium mb-3">Assign New Carrier</h4>
              <div className="flex space-x-3">
                <select
                  value={assignCarrierData.carrier_id}
                  onChange={(e) => setAssignCarrierData({...assignCarrierData, carrier_id: e.target.value})}
                  className="flex-1 border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Select Carrier</option>
                  {carriers.filter(c => !backendCarriers.find(bc => bc.carrier_id === c.carrier_id)).map((carrier) => (
                    <option key={carrier.carrier_id} value={carrier.carrier_id}>
                      {carrier.carrier_name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAssignCarrier}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Assign
                </button>
              </div>
            </div>

            {/* Current Carriers List */}
            <div>
              <h4 className="font-medium mb-3">Assigned Carriers ({backendCarriers.length})</h4>
              {backendCarriers.length > 0 ? (
                <div className="space-y-2">
                  {backendCarriers.map((carrier) => (
                    <div key={carrier.carrier_id} className="flex justify-between items-center p-3 bg-white border border-gray-200 rounded">
                      <div>
                        <div className="font-medium">{carrier.carrier_name}</div>
                        <div className="text-xs text-gray-500">
                          Assigned: {new Date(carrier.assigned_date * 1000).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveCarrier(carrier.carrier_id)}
                        className="px-3 py-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No carriers assigned yet.</p>
              )}
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setShowAssignCarrierModal(false);
                  setSelectedBackend(null);
                  setBackendCarriers([]);
                  setAssignCarrierData({ carrier_id: '', is_active: 1 });
                  setError(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBackendsPage;
